import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Loader2, CheckCircle2, X, ExternalLink, Ban } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("user_token") || localStorage.getItem("admin_token");
const fmtInt = (n) => Number(n || 0).toLocaleString("id-ID");

// Floating, app-wide indicator for the background "Refresh Tagihan" job. Re-checks every
// customer in a list live from PDAM; fully-paid ones are auto-marked "lunas". Runs in the
// background so the user can navigate away.
export default function TunggakanRefreshIndicator() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [finished, setFinished] = useState(null);
  const pollRef = useRef(null);
  const activeRef = useRef(false);

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    activeRef.current = false;
  }, []);

  const poll = useCallback(async () => {
    const t = getToken();
    if (!t) return;
    try {
      const res = await axios.get(`${API}/tunggakan/refresh/status`, { headers: { Authorization: `Bearer ${t}` } });
      const st = res.data || {};
      if (st.running) { setStatus(st); return; }
      if (st.finished_at && localStorage.getItem("tunggakan_refresh_active") === "1") {
        localStorage.setItem("tunggakan_refresh_active", "0");
        try { sessionStorage.setItem("tunggakan_refresh_finished", JSON.stringify(st)); } catch { /* ignore */ }
        setStatus(null);
        setFinished(st);
        stopPoll();
        window.dispatchEvent(new Event("tunggakan-refresh-done"));
        toast.success(`Refresh selesai — ${fmtInt(st.lunas)} lunas, ${fmtInt(st.updated)} diperbarui${st.failed ? `, ${fmtInt(st.failed)} gagal` : ""}.`);
      } else {
        setStatus(null);
        stopPoll();
      }
    } catch { /* keep polling silently */ }
  }, [stopPoll]);

  const startPolling = useCallback(() => {
    if (activeRef.current || !getToken()) return;
    activeRef.current = true;
    poll();
    pollRef.current = setInterval(poll, 2000);
  }, [poll]);

  useEffect(() => {
    if (localStorage.getItem("tunggakan_refresh_active") === "1" && getToken()) {
      startPolling();
    } else {
      try {
        const saved = sessionStorage.getItem("tunggakan_refresh_finished");
        if (saved) setFinished(JSON.parse(saved));
      } catch { /* ignore */ }
    }
    const onStarted = () => {
      try { sessionStorage.removeItem("tunggakan_refresh_finished"); } catch { /* ignore */ }
      setFinished(null);
      startPolling();
    };
    window.addEventListener("tunggakan-refresh-started", onStarted);
    return () => { window.removeEventListener("tunggakan-refresh-started", onStarted); stopPoll(); };
  }, [startPolling, stopPoll]);

  const dismissFinished = () => {
    try { sessionStorage.removeItem("tunggakan_refresh_finished"); } catch { /* ignore */ }
    setFinished(null);
  };

  const cancelRefresh = async () => {
    const t = getToken();
    try {
      await axios.post(`${API}/tunggakan/refresh/cancel`, {}, { headers: { Authorization: `Bearer ${t}` } });
      toast("Membatalkan refresh…");
    } catch { /* ignore */ }
  };

  if (!status && !finished) return null;

  const pct = status && status.total > 0 ? Math.round((status.processed / status.total) * 100) : 0;
  const phaseLabel =
    status?.phase === "retry" ? "Cek ulang yang gagal…" :
    status?.phase === "saving" ? "Menyimpan perubahan…" : "Refresh tagihan";

  return (
    <div className="fixed bottom-4 left-4 z-[130] w-[290px] max-w-[calc(100vw-2rem)]" data-testid="tunggakan-refresh-indicator">
      {status ? (
        <div className="bg-white rounded-2xl shadow-2xl border border-cyan-200 overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-600 to-sky-600 text-white px-3 py-2 flex items-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="font-bold text-sm flex-1">{phaseLabel}</span>
            <Loader2 className="w-4 h-4 animate-spin opacity-80" />
          </div>
          <div className="p-3 space-y-2">
            <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
              <div className="bg-cyan-600 h-2.5 transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs text-slate-600 font-medium" data-testid="refresh-indicator-progress">
              {fmtInt(status.processed)} / {fmtInt(status.total)} diperiksa ({pct}%)
            </div>
            <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
              <span>Lunas: <b className="text-emerald-600">{fmtInt(status.lunas)}</b></span>
              <span>· Diperbarui: <b className="text-cyan-600">{fmtInt(status.updated)}</b></span>
              {status.failed ? <span>· Gagal: <b className="text-amber-600">{fmtInt(status.failed)}</b></span> : null}
            </div>
            {status.current_id && (
              <div className="text-[11px] font-mono text-slate-400 truncate">Cek: {status.current_id}</div>
            )}
            <button
              onClick={cancelRefresh}
              className="w-full mt-1 text-xs font-semibold text-cyan-700 border border-cyan-200 rounded-lg py-1.5 hover:bg-cyan-50 flex items-center justify-center gap-1"
              data-testid="refresh-indicator-cancel-btn"
            >
              <Ban className="w-3.5 h-3.5" /> Batalkan
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-2xl border border-emerald-200 overflow-hidden" data-testid="refresh-indicator-done-card">
          <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-3 py-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <span className="font-bold text-sm flex-1">Refresh Selesai</span>
            <button onClick={dismissFinished} className="p-0.5 hover:bg-white/20 rounded" data-testid="refresh-indicator-dismiss-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-3 space-y-2">
            <div className="text-xs text-slate-600">
              <b className="text-emerald-600">{fmtInt(finished.lunas)}</b> ditandai lunas ·
              <b className="text-cyan-600"> {fmtInt(finished.updated)}</b> tagihan diperbarui
              {finished.failed ? <> · <b className="text-amber-600">{fmtInt(finished.failed)}</b> gagal cek</> : null}
            </div>
            {finished.list_id && (
              <button
                onClick={() => { navigate(`/tunggakan/${finished.list_id}`); dismissFinished(); }}
                className="w-full text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg py-2 flex items-center justify-center gap-1.5"
                data-testid="refresh-indicator-open-list-btn"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Buka Daftar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
